import { useEffect, useRef, useState } from "react";
import { Play, ExternalLink } from "lucide-react";
import { COMPANY } from "@/lib/content";

// Simple SVG icons for socials (Lucide has no brand icons)
function TikTokIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.01a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.44z" />
    </svg>
  );
}
function FacebookIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.9 3.77-3.9 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.78l-.44 2.9h-2.34V22c4.78-.79 8.44-4.93 8.44-9.94z" />
    </svg>
  );
}

export default function Social() {
  const embedContainerRef = useRef(null);
  const [showEmbed, setShowEmbed] = useState(false);

  // Only mount the TikTok embed (which pulls a big iframe + script) once the
  // section is close to the viewport. This avoids scroll-jank on the rest of the page.
  useEffect(() => {
    if (!embedContainerRef.current || typeof IntersectionObserver === "undefined") {
      // Fallback: load after a short idle time
      const t = setTimeout(() => setShowEmbed(true), 2500);
      return () => clearTimeout(t);
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShowEmbed(true);
            obs.disconnect();
          }
        });
      },
      { rootMargin: "400px 0px" }
    );
    obs.observe(embedContainerRef.current);
    return () => obs.disconnect();
  }, []);

  // Load TikTok embed script only once, and only after we decide to show the embed
  useEffect(() => {
    if (!showEmbed) return;
    if (document.querySelector('script[src*="tiktok.com/embed.js"]')) return;
    const s = document.createElement("script");
    s.src = "https://www.tiktok.com/embed.js";
    s.async = true;
    document.body.appendChild(s);
  }, [showEmbed]);

  return (
    <section
      id="kozossegi"
      data-testid="social-section"
      className="relative py-24 md:py-32 border-y border-gray-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-violet-600 font-bold mb-4">
              [ 06 ] · Kövess minket
            </div>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl leading-[0.95]">
              A kulisszák
              <br />
              <span className="text-gray-400">mögött.</span>
            </h2>
            <p className="mt-6 text-gray-500 leading-relaxed max-w-xl">
              Kövess minket TikTokon és Facebookon, ahol napi rendszerességgel osztunk meg
              helyszíni javításokat, hasznos tippeket és a mobilszervíz mindennapjait.
            </p>
          </div>
        </div>

        {/* Social profile tiles */}
        <div className="grid md:grid-cols-2 gap-5 mb-12">
          <a
            data-testid="social-tiktok-tile"
            href={COMPANY.tiktok}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative overflow-hidden border border-gray-200 bg-white p-8 md:p-10 rounded-sm hover:border-violet-400/60 hover:shadow-lg transition-all"
          >
            <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-gradient-to-br from-cyan-400/20 via-pink-500/20 to-transparent blur-2xl pointer-events-none" />
            <div className="relative flex items-start justify-between">
              <div>
                <div className="w-14 h-14 rounded-sm bg-gray-900 grid place-items-center mb-6">
                  <TikTokIcon className="w-7 h-7 text-white" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 font-bold font-mono">TikTok</div>
                <div className="font-display text-2xl md:text-3xl font-black text-gray-900 mt-1">
                  {COMPANY.tiktokHandle}
                </div>
                <p className="mt-4 text-sm text-gray-500 max-w-xs">
                  Rövid videók: defektjavítások, tippek, éjszakai kiszállások.
                </p>
              </div>
              <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-violet-600 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
            </div>
            <div className="relative mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-violet-600 font-bold">
              <Play className="w-3.5 h-3.5 fill-violet-300" /> Nézd meg a videókat
            </div>
          </a>

          <a
            data-testid="social-facebook-tile"
            href={COMPANY.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative overflow-hidden border border-gray-200 bg-white p-8 md:p-10 rounded-sm hover:border-blue-400/60 hover:shadow-lg transition-all"
          >
            <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-gradient-to-br from-blue-500/25 via-blue-400/15 to-transparent blur-2xl pointer-events-none" />
            <div className="relative flex items-start justify-between">
              <div>
                <div className="w-14 h-14 rounded-sm bg-[#1877F2] grid place-items-center mb-6">
                  <FacebookIcon className="w-7 h-7 text-white" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 font-bold font-mono">Facebook</div>
                <div className="font-display text-2xl md:text-3xl font-black text-gray-900 mt-1">
                  {COMPANY.facebookLabel}
                </div>
                <p className="mt-4 text-sm text-gray-500 max-w-xs">
                  Bejegyzések, ügyfélvélemények, akciók és elérhetőség.
                </p>
              </div>
              <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
            </div>
            <div className="relative mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-blue-600 font-bold">
              → Kövess minket
            </div>
          </a>
        </div>

        {/* TikTok profile embed */}
        <div className="border border-gray-200 bg-[#F0F1F5] p-6 md:p-8 rounded-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-violet-600 font-bold">Legutóbbi videóink</div>
              <div className="font-display text-xl md:text-2xl font-bold mt-1">TikTok · {COMPANY.tiktokHandle}</div>
            </div>
            <a
              href={COMPANY.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-tiktok-cta"
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 border border-violet-500/40 bg-violet-500/10 hover:bg-violet-500/20 text-gray-900 text-xs uppercase tracking-wider font-bold rounded-sm transition-all"
            >
              <TikTokIcon className="w-4 h-4" /> Összes videó
            </a>
          </div>

          <div
            ref={embedContainerRef}
            className="relative w-full overflow-hidden rounded-sm"
            style={{ minHeight: 620 }}
          >
            {showEmbed ? (
              <blockquote
                className="tiktok-embed"
                cite={COMPANY.tiktok}
                data-unique-id="pitstop8333"
                data-embed-from="embed_page"
                data-embed-type="creator"
                style={{ maxWidth: "780px", minWidth: "288px", margin: "0 auto" }}
              >
                <section>
                  <a target="_blank" rel="noreferrer" href={COMPANY.tiktok}>
                    {COMPANY.tiktokHandle}
                  </a>
                </section>
              </blockquote>
            ) : (
              <div className="w-full h-[620px] grid place-items-center border border-gray-200 bg-gray-100/[0.02] rounded-sm">
                <div className="flex flex-col items-center gap-3 text-gray-400">
                  <div className="w-12 h-12 rounded-full border-2 border-violet-500/30 border-t-violet-400 animate-spin" />
                  <div className="text-xs uppercase tracking-wider">Videók betöltése…</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
